import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material";
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { InlineAlert } from "src/grading/components/InlineAlert/InlineAlert";
import LoadingButton from "src/grading/components/LoadingButton/LoadingButton";
import { messageOf } from "src/grading/utils/apiError";
import { SchoolClass } from "src/models";
import { STUDENT_GRADES } from "src/models/students";
import { useCreateClassMutation } from "src/store/reducers/classes/classesApiSlice";

export interface CreateClassDialogProps {
  open: boolean;
  onClose: () => void;
  /** The school the class belongs to. Without it the dialog asks for one of `schools`. */
  schoolId?: string;
  schools?: { id: string; name: string }[];
  onCreated?: (created: SchoolClass) => void;
}

/** The school year that is running now: a new one starts in August. */
export const schoolYearNow = (now = new Date()) => {
  const y = now.getMonth() >= 7 ? now.getFullYear() : now.getFullYear() - 1;
  return `${y}-${y + 1}`;
};

/** Creates a class the school owns (`source = manual`), at a fixed or chosen school. */
export const CreateClassDialog = ({
  open,
  onClose,
  schoolId: fixedSchoolId,
  schools,
  onCreated,
}: CreateClassDialogProps) => {
  const { t } = useTranslation();
  const titleId = useId();
  const [createClass, { isLoading: creating }] = useCreateClassMutation();
  const [pickedSchool, setPickedSchool] = useState("");
  const [name, setName] = useState("");
  const [period, setPeriod] = useState("");
  const [grade, setGrade] = useState("");
  const [schoolYear, setSchoolYear] = useState(schoolYearNow());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName("");
    setPeriod("");
    setGrade("");
    setSchoolYear(schoolYearNow());
    setError(null);
  }, [open]);

  const schoolId = fixedSchoolId ?? pickedSchool;

  const submit = async () => {
    if (!schoolId) return;
    setError(null);
    try {
      const created = await createClass({
        schoolId,
        body: {
          name: name.trim(),
          period: period.trim() || undefined,
          gradeLevel: grade || undefined,
          schoolYear,
        },
      }).unwrap();
      onClose();
      onCreated?.(created);
    } catch (e) {
      setError(messageOf(e, t("school.createClassError")));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={creating ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby={titleId}
    >
      <DialogTitle id={titleId} component="h2">
        {t("school.addClass")}
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {fixedSchoolId ? null : (
          <TextField
            select
            label={t("school.school")}
            value={pickedSchool}
            onChange={(e) => setPickedSchool(e.target.value)}
            required
            sx={{ mt: 1 }}
          >
            {(schools ?? []).map((school) => (
              <MenuItem key={school.id} value={school.id}>
                {school.name}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          label={t("school.className")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          autoFocus
          inputProps={{ maxLength: 120 }}
          sx={fixedSchoolId ? { mt: 1 } : undefined}
        />
        <TextField
          label={t("school.colPeriod")}
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          inputProps={{ maxLength: 40 }}
        />
        <TextField
          select
          label={t("school.colGrade")}
          value={grade}
          onChange={(e) => setGrade(e.target.value)}
        >
          <MenuItem value="">{t("school.gradeMixed")}</MenuItem>
          {STUDENT_GRADES.map((g) => (
            <MenuItem key={g} value={g}>
              {g}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label={t("school.schoolYear")}
          value={schoolYear}
          onChange={(e) => setSchoolYear(e.target.value)}
          helperText={t("school.schoolYearHelper")}
        />
        {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={creating}>
          {t("common.cancel")}
        </Button>
        <LoadingButton
          variant="contained"
          loading={creating}
          disabled={
            !schoolId || !name.trim() || !/^\d{4}-\d{4}$/.test(schoolYear)
          }
          onClick={submit}
          label={t("common.create")}
        />
      </DialogActions>
    </Dialog>
  );
};

export default CreateClassDialog;
